import {
  ContentItem
} from 'interfaces'
import {
  BRAND_BLUESKY,
  BRAND_PINTEREST,
  BRAND_THREADS,
  BRAND_TWITTER,
  BRAND_FACEBOOK,
  BRAND_INSTAGRAM,
  BRAND_LINKEDIN,
  BRAND_GOOGLE,
  BRAND_TIKTOK,
  BRAND_YOUTUBE
} from 'shared'
import {
  BackgroundTemplate,
  COMPOSER_STATUS_KEY_GENERIC,
  ComposerBasePost,
  ComposerFacebookPost,
  ComposerInstagramPost,
  ComposerLinkedinPost,
  ComposerPinterestPost,
  ComposerStateShared,
  ComposerTikTokPost,
  ComposerTwitterPost,
  ComposerYoutubePost,
  CreatePostRequestData,
  GOOGLE_POST_TYPE_UPDATE,
  PostDestinationType,
  Prompt
} from 'shared/types'
import { MS_IN_DAY } from 'utils/constants'
import { ArticleBase } from './interfaces/ArticleBase'
import { AIConversation } from './suggest/net'
import { randomId } from './utils'
import { REPEAT_PERIOD_WEEKS } from 'shared'

export const COMPOSER_STATE_VERSION = 'composer:15'
export type ContentCardComposeAction = 'create' | 'carousel' | 'multipost'

export const emptyStatus = {
  text: '',
  html: '',
  touched: false
}

const DEFAULT_SCHEDULE_TIME_OFFSET_MINUTES = 60

export const getDefaultScheduleTime = () => {
  const time = new Date()
  time.setMinutes(time.getMinutes() + DEFAULT_SCHEDULE_TIME_OFFSET_MINUTES)
  return time
}

const defaultScheduleTime = getDefaultScheduleTime()
export const defaultScheduleTimeString = defaultScheduleTime.toISOString()
// eslint-disable-next-line no-magic-numbers
export const defaultGoogleEventEndTime = new Date(defaultScheduleTime.getTime() + MS_IN_DAY * 30).toISOString()

export type PostNetworkData = ComposerBasePost
  | ComposerFacebookPost
  | ComposerTwitterPost
  | ComposerInstagramPost
  | ComposerPinterestPost
  | ComposerYoutubePost
  | ComposerTikTokPost
  | ComposerLinkedinPost

export interface BulkContentPost {
  id: string
  network: PostDestinationType
  imageUrl?: string
  videoUrl?: string
  videoThumbnailUrl?: string
  article?: ArticleBase
  content?: ContentItem
  imageUpload?: { file: File, url?: string, src?: string, error?: string }
  data: PostNetworkData
  order: number
  recycle: boolean | Date | number
  /** The row's AI content checkbox. Kept on the row, not in `data`, so TikTok - whose flag is per profile - works the same. */
  isAiContent?: boolean
  error?: {
    message: string
    code: string
  }
}

export type MediaItemState = {
  id: string
  isVideo?: boolean
  url?: string
  size?: number
  error?: string
  isUploading?: boolean
  isFeatured?: boolean
  isEdited?: boolean
  order?: number
  orientation?: 'landscape' | 'portrait'
  width?: number
  height?: number
}

export type ComposerVideoState = {
  id: string
  url?: string
  duration?: number
  size?: number
  error?: string
  isEdited?: boolean
  thumbnailUrl?: string
  thumbnailOffset?: number
  isUploading?: boolean
  metadata?: {
    width?: number
    height?: number
    fileType?: string
  }
}

export type PostEditType = 'queued' | 'pending' | 'scheduled' | 'planned'

export interface ComposerState extends ComposerStateShared {
  multiPost: {
    posts: Record<string, BulkContentPost>
  }
  useGenericStatus: boolean
  draft?: {
    id: string
    updatedAt: string
  }
  images: Record<string, MediaItemState>
  video?: ComposerVideoState
  postAsPhoto: boolean
  bulkUploadFile?: {
    url: string
    rows: number
    name: string
  }
  contentItem?: ContentItem
  selectedBucketId?: string

  activeTab: PostDestinationType
  networksView: 'list' | 'tabs'
  progress: {
    [id: string]: {
      post: CreatePostRequestData
      error?: string
      complete?: boolean
    }
  }
  resetKey: number
  savedStatuses: any[]
  rememberIGLocations: boolean
  postEdit?: {
    id: string
    ppid: string
    type: PostEditType
    bucketId?: string
  }
  prompt: {
    content?: ContentItem
    action?: ContentCardComposeAction
  }
  writer: {
    favoritePrompts: Prompt[]
    userPrompts: Prompt[]
    aiWriterPromptId?: string
    imageRequests: number
    imageRequestsLimit: number
  }
  facebookBackgroundTemplates: BackgroundTemplate[]
  defaultFacebookBackgroundTemplateId?: string
  documentUpload?: {
    url: string
    name: string
  }
  altTextEnabled: boolean
  altText: string
  /**
   * The composer-level AI content checkbox - the only copy of the flag. `getPostData` writes it into every network's
   * `isAiContent`, including each TikTok profile's settings, when the post is serialized.
   */
  aiContent: boolean
  aiChats: AIConversation[]
  lastCommentDelay: number
}

export function initialState(): ComposerState {
  return {
    posts: {
      [BRAND_BLUESKY]: {
        status: { ...emptyStatus },
        comments: [{ id: randomId(), text: '', delay: 0, html: '' }]
      },
      [BRAND_FACEBOOK]: {
        albums: [],
        reshare: false,
        status: { ...emptyStatus },
        comments: [{ id: randomId(), text: '', html: '', delay: 0 }],
        lastUsedVideoPostType: 'post'
      },
      [BRAND_TWITTER]: {
        status: { ...emptyStatus },
        comments: [{ id: randomId(), text: '', delay: 0, html: '' }],
        tweetType: 'new'
      },
      [BRAND_PINTEREST]: {
        status: { ...emptyStatus },
        title: '',
        description: '',
        destinationUrl: ''
      },
      [BRAND_INSTAGRAM]: {
        status: { ...emptyStatus },
        comments: [{ id: randomId(), text: '', delay: 0, html: '' }],
        locations: {}
      },
      [BRAND_LINKEDIN]: {
        status: { ...emptyStatus },
        comments: [{ id: randomId(), text: '', delay: 0, html: '' }]
      },
      [BRAND_TIKTOK]: {
        status: { ...emptyStatus },
        settings: {},
        title: '',
        description: ''
      },
      [BRAND_GOOGLE]: {
        status: { ...emptyStatus },
        selectedPostType: GOOGLE_POST_TYPE_UPDATE,
        update: {
          link: ''
        },
        offer: {
          offer: {
            redeemOnlineUrl: ''
          },
          event: {
            title: '',
            schedule: {
              startDate: defaultScheduleTimeString,
              endDate: defaultGoogleEventEndTime
            }
          }
        },
        event: {
          title: '',
          schedule: {
            startDate: defaultScheduleTimeString,
            endDate: defaultGoogleEventEndTime
          }
        }
      },
      [BRAND_YOUTUBE]: {
        status: { ...emptyStatus },
        title: '',
        description: '',
        privacyStatus: 'public',
        comments: [{ id: randomId(), text: '', delay: 0, html: '' }]
      },
      [BRAND_THREADS]: {
        status: { ...emptyStatus },
        comments: [{ id: randomId(), text: '', delay: 0, html: '' }]
      },
      [COMPOSER_STATUS_KEY_GENERIC]: {
        status: { ...emptyStatus }
      }
    },
    images: {},
    recycle: false,
    selectedProfiles: [],
    progress: {},
    activeTab: BRAND_FACEBOOK,
    networksView: 'list',
    resetKey: 0,
    links: [],
    savedStatuses: [],
    pinterestBoards: {},
    postAsPhoto: false,
    schedule: {
      isActive: false,
      date: defaultScheduleTimeString,
      time: defaultScheduleTimeString,
      repeat: false,
      repeatCount: 1,
      repeatPeriod: REPEAT_PERIOD_WEEKS,
      repeatUntil: 'forever',
      repeatUntilDate: defaultScheduleTimeString
    },
    multiPost: {
      posts: {}
    },
    prompt: {},
    useGenericStatus: true,
    rememberIGLocations: true,
    writer: {
      favoritePrompts: [],
      userPrompts: [],
      imageRequests: 0,
      imageRequestsLimit: 1
    },
    draft: undefined,
    facebookBackgroundTemplates: [],
    documentUpload: undefined,
    altTextEnabled: false,
    altText: '',
    aiContent: false,
    queuePostAction: 'bottom',
    aiChats: [],
    lastCommentDelay: 0
  }
}

export default ComposerState
